import { useCallback, useMemo } from 'react';
import { keyframes } from 'styled-components';
import { ThumbsDownIcon } from 'foundations-assets/icons/components/ThumbsDownIcon';
import { ThumbsUpIcon } from 'foundations-assets/icons/components/ThumbsUpIcon';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import { Flex } from 'foundations-components/components/Flex';
import styled from 'foundations-theming-specialty/utils';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { CopyIcon } from 'foundations-assets/icons/components/CopyIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const zoom = keyframes(["0%{transform:scale(0.8);opacity:1;}70%{transform:scale(1.4);opacity:1;}100%{transform:scale(1);opacity:1;}"]);
const InteractionIconBackground = styled.div.withConfig({
  displayName: "InteractionIconButton__InteractionIconBackground"
})(["cursor:", ";align-items:center;background-color:", ";border-radius:", ";display:flex;justify-content:center;width:", ";height:", ";& svg{&:active{animation:", " 300ms cubic-bezier(0.42,0,0.58,1);animation-fill-mode:forwards;}color:", ";}&:hover{background-color:", ";}"], ({
  disabled
}) => disabled ? 'not-allowed' : 'pointer', ({
  checked,
  disabled,
  theme,
  backgroundColor
}) => {
  if (disabled) {
    return `${theme.color['fill-primary-disabled']} !important`;
  }
  if (checked) {
    return theme.color['fill-accent-neutral-default'];
  }
  return theme.color[backgroundColor || 'fill-surface-default'];
}, ({
  theme
}) => theme.borderRadius[100], ({
  theme
}) => theme.space[800], ({
  theme
}) => theme.space[800], zoom, ({
  checked,
  disabled,
  theme
}) => {
  if (disabled) {
    return theme.color['icon-core-disabled'];
  }
  if (checked) {
    return theme.color['fill-surface-default'];
  }
  return theme.color['icon-core-default'];
}, ({
  checked,
  theme
}) => checked ? theme.color['fill-accent-neutral-hover'] : theme.color['fill-field-hover']);
const ContainerToCenterIcon = ({
  children
}) => {
  return /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    justifyContent: "center",
    children: children
  });
};
const SimplifiedCopyButton = ({
  dataTestId,
  disabled,
  value,
  children
}) => {
  return /*#__PURE__*/_jsx(CopyButton, {
    "data-test-id": dataTestId,
    disabled: disabled,
    hint: () => null,
    hintSuccess: () => null,
    value: value,
    use: "unstyled",
    children: children
  });
};
const STANDARD_ICON_SIZE = '300';
const getIcon = ({
  icon,
  disabled,
  valueToCopy
}) => {
  switch (icon) {
    case 'thumbsUp':
      return () => /*#__PURE__*/_jsx(ContainerToCenterIcon, {
        children: /*#__PURE__*/_jsx(ThumbsUpIcon, {
          size: STANDARD_ICON_SIZE
        })
      });
    case 'thumbsDown':
      return () => /*#__PURE__*/_jsx(ContainerToCenterIcon, {
        children: /*#__PURE__*/_jsx(ThumbsDownIcon, {
          size: STANDARD_ICON_SIZE
        })
      });
    case 'externalLink':
      return () => /*#__PURE__*/_jsx(ContainerToCenterIcon, {
        children: /*#__PURE__*/_jsx(ExternalLinkIcon, {
          size: STANDARD_ICON_SIZE
        })
      });
    case 'copy':
      return () => /*#__PURE__*/_jsx(SimplifiedCopyButton, {
        disabled: disabled,
        value: valueToCopy,
        children: /*#__PURE__*/_jsx(ContainerToCenterIcon, {
          children: /*#__PURE__*/_jsx(CopyIcon, {
            size: STANDARD_ICON_SIZE
          })
        })
      });
    default:
      return () => null;
  }
};
const InteractionIconButton = ({
  ariaLabel,
  checked,
  disabled = false,
  icon,
  onClick,
  dataTestId,
  valueToCopy,
  iconBackgroundStyleProps
}) => {
  const MemoizedIcon = useMemo(
  // eslint-disable-next-line react-hooks/static-components
  () => {
    return getIcon({
      disabled,
      icon,
      valueToCopy
    });
  }, [disabled, icon, valueToCopy]);
  const handleOnClick = useCallback(event => {
    // prevent the event from propagating and causing potential double-clicks
    event.preventDefault();
    event.stopPropagation();

    // Don't trigger onClick if the button is disabled
    if (!disabled) {
      onClick === null || onClick === void 0 || onClick();
    }
  }, [onClick, disabled]);
  return /*#__PURE__*/_jsx(InteractionIconBackground, {
    "data-test-id": dataTestId,
    "aria-label": ariaLabel,
    "aria-checked": checked,
    checked: checked,
    disabled: disabled,
    onClick: handleOnClick,
    backgroundColor: iconBackgroundStyleProps === null || iconBackgroundStyleProps === void 0 ? void 0 : iconBackgroundStyleProps.backgroundColor,
    children: /*#__PURE__*/_jsx(MemoizedIcon, {})
  });
};
export default InteractionIconButton;